import { useEffect, useRef, useState, useCallback } from "react";

export const useInfiniteAsyncScroll = ({
  idField = "id",
  params,
  onScroll,
  take = 20,
  scrollContainerRef,
  mapResponse
}) => {
  const [items, setItems] = useState([]);
  const [additionalData, setAdditionalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const skipRef = useRef(0);
  const totalRef = useRef(null);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current || !hasMoreRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await onScroll({
        params,
        skip: skipRef.current,
        take,
      });

      const { newItems, total, additionalData } = mapResponse(result);

      totalRef.current = total;

      setItems((prev) => {
        const map = new Map(prev.map((item) => [item[idField], item]));
        newItems.forEach((item) => map.set(item[idField], item));
        return Array.from(map.values());
      });

      setAdditionalData(additionalData);

      const currentLength = skipRef.current + newItems.length;

      if (!newItems.length || (totalRef.current !== null && currentLength >= totalRef.current)) {
        hasMoreRef.current = false;
      } else {
        hasMoreRef.current = newItems.length === take;
      }

      skipRef.current += take;
    } catch (err) {
      setError(err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [onScroll, params, take, mapResponse, idField]);

  useEffect(() => {
    skipRef.current = 0;
    totalRef.current = null;
    hasMoreRef.current = true;
    setItems([]);

    fetchData();
  }, [params]);

  useEffect(() => {
    const container = scrollContainerRef?.current || window;

    const handleScroll = () => {
      if (isFetchingRef.current || !hasMoreRef.current) return;

      let scrollTop, scrollHeight, clientHeight;

      if (container === window) {
        scrollTop = window.scrollY;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      } else {
        scrollTop = container.scrollTop;
        scrollHeight = container.scrollHeight;
        clientHeight = container.clientHeight;
      }

      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom) {
        fetchData();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [fetchData, scrollContainerRef]);

  return {
    items,
    loading,
    error,
    hasMore: hasMoreRef.current,
    total: totalRef.current,
    additionalData,
  };
};
